import { z } from 'zod';

import { ApiError, apiRequest, type RequestOptions } from '@/api/client';
import { pageSchema } from '@/api/types';
import { focalPointSchema } from '@/features/storefront/sections/schema';
import { resolveStorefrontHost } from '@/features/storefront/storefrontHost';
import {
  storefrontLanguageHeaders,
  storefrontUrlLanguage,
} from '@/features/storefront/storefrontLanguageHeader';
import { themeSchema } from '@/features/storefront/themeSchema';

/**
 * Public storefront API.
 *
 * The backend resolves which store to serve from the hostname. When the page
 * is not on the store's own hostname - a preview opened from the dashboard, or
 * a dev server serving both - `X-Storefront-Host` names the store instead. See
 * `storefrontHost.ts` for where that name comes from.
 */
function storefrontHeaders(host?: string | null): Record<string, string> {
  // An explicit host is the dashboard asking about a store it already knows -
  // the theme customiser previewing this merchant's own shop. It is passed in
  // rather than resolved, because the dashboard's own URL says nothing about
  // which store is being designed.
  const resolved = host ?? resolveStorefrontHost();
  return {
    ...(resolved ? { 'X-Storefront-Host': resolved } : {}),
    // The shopper's language choice, when one has been made. See
    // `storefrontLanguageHeader.ts`.
    ...storefrontLanguageHeaders(),
  };
}

export {
  getStorefrontLanguageHeader,
  setResolvedStorefrontLanguage,
  setStorefrontLanguageHeader,
} from '@/features/storefront/storefrontLanguageHeader';

/**
 * Options for a public storefront call.
 *
 * `auth: 'none'` matters when a *merchant* is previewing their own shop: their
 * dashboard token is live in this tab, and there is no reason for a product
 * listing or a cart to carry it. These endpoints do not read it, and one that
 * later did would then be reading the wrong person's identity.
 */
function publicOptions(extra: RequestOptions = {}, host?: string | null): RequestOptions {
  return {
    // The same URL answers for every shop - the store is named in a header -
    // so a response the browser kept for one shop must never be handed back
    // for another. A "moved" answer for an old name did exactly that.
    cache: 'no-store',
    ...extra,
    auth: 'none',
    headers: { ...storefrontHeaders(host), ...extra.headers },
  };
}

/**
 * A public read a CDN may cache: the live shop and its language in the URL.
 *
 * Cloudflare keys its cache on the URL alone, and the shop is otherwise named
 * only in `X-Storefront-Host`. With `_host` and `lang` in the query the
 * server marks the answer cacheable for a few seconds and replies to it for
 * any origin, so it is fetched without cookies (`credentials: 'omit'`) - none
 * of these endpoints read one. A preview naming its own `host`, or a page
 * that does not know its language yet, asks exactly as before.
 */
function publicRead(path: string, host?: string | null): [string, RequestOptions] {
  const shop = host === undefined ? resolveStorefrontHost() : null;
  const language = storefrontUrlLanguage();
  if (!shop || !language) return [path, publicOptions({}, host)];
  const edge = new URLSearchParams({ _host: shop, lang: language });
  const separator = path.endsWith('?') ? '' : path.includes('?') ? '&' : '?';
  return [`${path}${separator}${edge}`, publicOptions({ credentials: 'omit' }, host)];
}

export { themeSchema, type StorefrontTheme } from '@/features/storefront/themeSchema';

/** A menu link and its children, mirroring `MenuItem` on the server. */
export interface StorefrontMenuItem {
  label: string;
  url: string;
  items: StorefrontMenuItem[];
}

export const menuItemSchema: z.ZodType<StorefrontMenuItem> = z.lazy(() =>
  z.object({ label: z.string(), url: z.string(), items: z.array(menuItemSchema) }),
);

export const menuSchema = z.object({
  handle: z.string(),
  name: z.string(),
  items: z.array(menuItemSchema),
});

/**
 * Which parts of the shop exist, mirroring `StorefrontFeatures` on the server.
 *
 * Every field defaults on, so a context from a server that predates this - or
 * a fixture written before it - reads as a whole shop rather than an empty one.
 */
export const storefrontFeaturesSchema = z.object({
  cart: z.boolean().default(true),
  checkout: z.boolean().default(true),
  accounts: z.boolean().default(true),
  search: z.boolean().default(true),
  blog: z.boolean().default(true),
  /**
   * The `/careers` pages and their links. Off unless the merchant turns it
   * on: unlike the cart, a shop never had a careers page to keep.
   */
  careers: z.boolean().default(false),
  enquiry: z
    .object({
      label: z.string().default('Enquire now'),
      target: z.string().default('/pages/contact'),
    })
    .default({}),
  /**
   * The merchant's say on the card's hover photo. Absent or null follows the
   * theme's `cards.hover_image`, so an older context or fixture is unchanged.
   */
  hover_image: z.boolean().nullish(),
});

export type StorefrontFeatures = z.infer<typeof storefrontFeaturesSchema>;

/** The ways a shopper can pay, mirroring `PaymentMethod` on the server. */
export const CHECKOUT_METHODS = ['RAZORPAY', 'COD', 'WHATSAPP'] as const;
export type CheckoutMethod = (typeof CHECKOUT_METHODS)[number];

/**
 * One way of paying that this shop offers, mirroring `StorefrontCheckoutOption`.
 *
 * Already filtered on the server: only the options the merchant switched on
 * *and* finished configuring arrive here, in the order they chose. COD carries
 * its fee and its order-value bounds so the till can grey it out with a reason
 * rather than let the API refuse it after the shopper has pressed the button.
 */
export const checkoutOptionSchema = z.object({
  method: z.enum(CHECKOUT_METHODS),
  instructions: z.string().nullish().transform((value) => value ?? null),
  fee: z.string().default('0.00'),
  min_order_amount: z.string().nullish().transform((value) => value ?? null),
  max_order_amount: z.string().nullish().transform((value) => value ?? null),
  whatsapp_phone: z.string().nullish().transform((value) => value ?? null),
});

export type CheckoutOption = z.infer<typeof checkoutOptionSchema>;

/**
 * Where the browser finds this shop as an installable app.
 *
 * Handed down by the server rather than assembled here, and the reason is
 * origins: a manifest has to be fetched from the shop's own origin or the
 * browser will not associate it with the page, and this bundle only knows the
 * API by whatever `VITE_API_BASE_URL` it was built with. The server knows
 * which shop it is answering as.
 *
 * Nullable so a storefront newer than its server still renders: no PWA block
 * is a shop that cannot be installed, not a shop that fails to load.
 */
export const storefrontPwaSchema = z.object({
  manifest_url: z.string(),
  // What iOS writes under the home screen icon. Defaulted so a context
  // from a server that predates it still parses.
  short_name: z.string().default(''),
  apple_touch_icon_url: z.string(),
  theme_color: z.string(),
  background_color: z.string(),
  scope: z.string(),
  // The scheme the installed app registers, which opens it from a tab.
  // Optional so a context from a server that predates it still parses.
  open_protocol: z.string().optional(),
  // The key a browser subscribes to order notifications with. Null (or, from
  // an older server, absent) when this shop does not offer them.
  push_public_key: z.string().nullish(),
});

export type StorefrontPwa = z.infer<typeof storefrontPwaSchema>;

/**
 * How this shop delivers, mirroring `StorefrontShippingInfo` on the server.
 *
 * `enabled` is the whole switch: off, and the till behaves exactly as it did
 * before delivery was priced at all - a flat line in the summary and nothing
 * asked. `requires_pincode` is the difference between a shop whose delivery is
 * a flat rate and one whose couriers have to be asked, and it decides whether
 * the summary can name a number before the shopper has typed where they are.
 */
export const storefrontShippingInfoSchema = z.object({
  enabled: z.boolean().default(false),
  requires_pincode: z.boolean().default(false),
  cod_by_pincode: z.boolean().default(false),
  free_shipping_threshold: z.string().nullish().transform((value) => value ?? null),
  flat_rate: z.string().nullish().transform((value) => value ?? null),
});

export type StorefrontShippingInfo = z.infer<typeof storefrontShippingInfoSchema>;

/** One way of getting the parcel there, mirroring `StorefrontShippingOption`. */
export const shippingOptionSchema = z.object({
  code: z.string(),
  name: z.string(),
  courier_name: z.string().nullish().transform((value) => value ?? null),
  amount: z.string(),
  estimated_days: z.number().nullish().transform((value) => value ?? null),
  /** A date, `YYYY-MM-DD`. */
  estimated_delivery: z.string().nullish().transform((value) => value ?? null),
  cod_available: z.boolean().default(true),
  description: z.string().nullish().transform((value) => value ?? null),
});

export type ShippingOption = z.infer<typeof shippingOptionSchema>;

/**
 * What delivery to one PIN code costs, mirroring `StorefrontShippingQuote`.
 *
 * `serviceable: false` is an answer, not an error: the couriers do not go
 * there, and `message` says so in words the shopper can read. `options` is
 * already priced for this basket and this payment method, which is why the
 * checkout asks again whenever either changes.
 */
export const shippingQuoteSchema = z.object({
  postal_code: z.string(),
  serviceable: z.boolean(),
  options: z.array(shippingOptionSchema).default([]),
  cod_available: z.boolean().default(true),
  message: z.string().nullish().transform((value) => value ?? null),
  selected_code: z.string().nullish().transform((value) => value ?? null),
  free_shipping_threshold: z.string().nullish().transform((value) => value ?? null),
  free_shipping_remaining: z.string().nullish().transform((value) => value ?? null),
});

export type ShippingQuote = z.infer<typeof shippingQuoteSchema>;

/**
 * A language the shop is published in, mirroring `StorefrontLanguage` on the
 * server (docs/i18n.md §7). `direction` arrives upper-case, as stored.
 */
export const storefrontLanguageSchema = z.object({
  code: z.string(),
  locale: z.string(),
  name: z.string(),
  native_name: z.string(),
  direction: z
    .string()
    .transform((value): 'LTR' | 'RTL' => (value.toUpperCase() === 'RTL' ? 'RTL' : 'LTR'))
    .default('LTR'),
});

export type StorefrontLanguage = z.infer<typeof storefrontLanguageSchema>;

/** The shop's language behaviour. Defaults are what a server without i18n means. */
export const storefrontI18nSchema = z.object({
  switcher_enabled: z.boolean().default(true),
  fallback_to_default: z.boolean().default(true),
});

export type StorefrontI18nSettings = z.infer<typeof storefrontI18nSchema>;

/**
 * The shop's WhatsApp channel, as the storefront sees it.
 *
 * Present only when the merchant has connected a WhatsApp Business account and
 * their plan allows selling on it; `null` otherwise, which every page reads as
 * "draw nothing". The switches are the merchant's own: which of the buttons
 * and cards the shop offers. `phone` is bare digits with the country code,
 * which is what `wa.me` wants; `display_phone` is how the merchant writes it.
 */
export const storefrontWhatsAppSchema = z.object({
  enabled: z.boolean().default(false),
  phone: z.string().default(''),
  display_phone: z.string().default(''),
  chat_url: z.string().nullish().transform((value) => value ?? null),
  buy_on_whatsapp: z.boolean().default(false),
  chat_on_whatsapp: z.boolean().default(false),
  share_on_whatsapp: z.boolean().default(false),
  help_on_cart_checkout: z.boolean().default(false),
  continue_checkout_on_whatsapp: z.boolean().default(false),
  // The message a "Buy on WhatsApp" button starts with; `{{product_name}}` is
  // replaced by the product being looked at.
  buy_prefill: z.string().default(''),
  support_greeting: z.string().default(''),
});

export type StorefrontWhatsApp = z.infer<typeof storefrontWhatsAppSchema>;

/** The seller block: legal name, address, GSTIN/FSSAI and customer care. */
export const storefrontSellerSchema = z.object({
  name: z.string(),
  address: z.string().nullish(),
  gstin: z.string().nullish(),
  fssai_number: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
});
export type StorefrontSeller = z.infer<typeof storefrontSellerSchema>;

export const contextSchema = z.object({
  store_name: z.string(),
  description: z.string().nullable(),
  logo_url: z.string().nullable(),
  favicon_url: z.string().nullish().transform((value) => value ?? null),
  currency: z.string(),
  seo_title: z.string().nullable(),
  seo_description: z.string().nullable(),
  theme: themeSchema,
  // The merchant's navigation, built under Content.
  // False when the merchant has not connected their own gateway. A shop that
  // takes cash on delivery only is a real shop, not a broken one.
  online_payments_enabled: z.boolean().default(false),
  // The payment options the till shows, in the merchant's order. Absent on a
  // context from a server that predates it - see `resolveCheckoutOptions`,
  // which then falls back to what `online_payments_enabled` implies.
  checkout_options: z.array(checkoutOptionSchema).optional(),
  // Which pages and features this merchant actually uses. The shell needs it
  // before it draws: a header that shows a cart link and takes it away on the
  // next tick reads as broken.
  storefront_features: storefrontFeaturesSchema.default({}),
  menus: z.array(menuSchema).default([]),
  // Absent on a context from a server that predates the installable
  // storefront - see `storefrontPwaSchema`.
  pwa: storefrontPwaSchema.nullish().transform((value) => value ?? null),
  // Absent on a context from a server that predates priced delivery - see
  // `resolveShipping`, which reads that as delivery switched off.
  shipping: storefrontShippingInfoSchema.nullish().transform((value) => value ?? null),
  // Multi-language (docs/i18n.md §7). Optional rather than defaulted, so a
  // context from a server that predates it - or a preview source or fixture
  // built by hand - is still a whole context: an English-only shop with no
  // switcher. Read them through `resolveStorefrontLanguages`.
  language: storefrontLanguageSchema.nullish(),
  default_language: z.string().nullish(),
  languages: z.array(storefrontLanguageSchema).nullish(),
  // `StorefrontI18nSettings | None` on the server: null is a real answer.
  i18n: storefrontI18nSchema.nullish(),
  // The WhatsApp channel, or null for a shop without one. Optional rather
  // than defaulted so a context from a server that predates it - or a
  // fixture built by hand - is still a whole context.
  whatsapp: storefrontWhatsAppSchema.nullable().optional(),
  // True when the shop sends appointment updates on WhatsApp - connected,
  // with at least one appointment message switched on - so the booking form
  // offers the shopper the choice. Absent (an older server, a hand-built
  // fixture) reads as false; see `bookingWhatsApp`.
  booking_whatsapp: z.boolean().optional(),
  // The shop's own seller details for the product page's seller panel. Null
  // until the merchant switches on "Show seller details on product pages";
  // absent from an older server.
  seller: storefrontSellerSchema.nullish(),
});

/** A metaobject entry the merchant pointed this product at. */
export const storefrontMetaobjectSchema = z.object({
  handle: z.string(),
  display_name: z.string(),
  values: z.record(z.unknown()),
});

export const storefrontMetafieldSchema = z.object({
  key: z.string(),
  name: z.string(),
  field_type: z.string(),
  is_list: z.boolean(),
  value: z.unknown().nullable(),
  entries: z.array(storefrontMetaobjectSchema),
});

/**
 * A product's published reviews, summed up: the average to one decimal and how
 * many there are. Null and zero on a shop whose plan has no reviews, and
 * absent from a server older than reviews - so both are optional, and a
 * reader asks `(rating_count ?? 0) > 0` rather than trusting either to be set.
 */
const ratingFields = {
  rating_average: z.number().nullish(),
  rating_count: z.number().nullish(),
};

export const productSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  short_description: z.string().nullable(),
  price: z.string(),
  compare_at_price: z.string().nullable(),
  image_url: z.string().nullable(),
  // The first photo's focal point, so a card crops it as the product page
  // does (`focalStyle`); null is the middle.
  image_focal: focalPointSchema.nullish(),
  in_stock: z.boolean(),
  metafields: z.array(storefrontMetafieldSchema).default([]),
  ...ratingFields,
  // How many colours it comes in, for a card's "6 colours" line; 0 or absent for none.
  colour_count: z.number().optional(),
  // What a card needs for the theme's `cards` settings - all optional, so a
  // sample shop or an older server without them still parses. The second
  // photo for a hover swap; up to five colour names for swatch dots
  // (`colour_count` is the full count, for "+n"); when it was added, for a
  // "New" badge; whether it is a top seller; and the variant a quick add buys.
  hover_image_url: z.string().nullish(),
  colours: z.array(z.string()).optional(),
  created_at: z.string().nullish(),
  is_bestseller: z.boolean().optional(),
  variant_count: z.number().optional(),
  default_variant_id: z.string().nullish(),
});

export const storefrontCategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  image_url: z.string().nullable(),
  // Where a cropping tile holds the picture (`focalStyle`); absent or null is
  // the middle. Optional, so a hand-built category (a theme's sample shelf)
  // needs no point.
  image_focal: focalPointSchema.nullish(),
  parent_id: z.string().nullable(),
  position: z.number(),
});

/**
 * Which language a storefront response's merchant text is in; `fallback` is
 * true when some of it fell back to the default language (docs/i18n.md §7).
 */
export const localizedInfoSchema = z.object({
  language: z.string(),
  fallback: z.boolean().default(false),
});

export const productDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  short_description: z.string().nullable(),
  description: z.string().nullable(),
  price: z.string(),
  compare_at_price: z.string().nullable(),
  brand: z.string().nullable(),
  images: z.array(
    z.object({
      id: z.string(),
      url: z.string(),
      alt_text: z.string().nullable(),
      // Which part of the photo the square gallery keeps in view.
      image_focal: focalPointSchema.nullish(),
    }),
  ),
  variants: z.array(
    z.object({
      id: z.string(),
      options: z.record(z.unknown()),
      price: z.string(),
      in_stock: z.boolean(),
      // Defaulted, so a storefront served by a deploy older than the one that
      // started publishing it renders a product page with no SKU line rather
      // than no product page.
      sku: z.string().default(''),
      // How many are left, only while the live theme carries a low-stock bar,
      // the stock is tracked and the number is small. Null everywhere else.
      // Optional, so a variant built by hand (a placeholder, a theme's
      // sample product) needs none.
      stock_left: z.number().nullish(),
    }),
  ),
  metafields: z.array(storefrontMetafieldSchema).default([]),
  // Returns, refunds and replacements for this product, the store's policy
  // and the product's own already combined. Null when the shop offers none.
  after_sale: z
    .object({
      returnable: z.boolean(),
      refundable: z.boolean(),
      replaceable: z.boolean(),
      window_days: z.number(),
      note: z.string().nullish(),
    })
    .nullish()
    .catch(null),
  // The product's own search listing, in the resolved language. Optional: a
  // server that does not send them leaves the tab named after the product.
  seo_title: z.string().nullish(),
  seo_description: z.string().nullish(),
  // Which language the text is in, and whether any of it fell back to the
  // default language (docs/i18n.md §7).
  localized: localizedInfoSchema.nullish(),
  ...ratingFields,
});

export const cartSchema = z.object({
  token: z.string(),
  items: z.array(
    z.object({
      variant_id: z.string(),
      product_name: z.string(),
      product_slug: z.string(),
      image_url: z.string().nullable(),
      // The thumbnail's focal point, read live with the picture.
      image_focal: focalPointSchema.nullish(),
      options: z.record(z.unknown()),
      sku: z.string(),
      quantity: z.number(),
      unit_price: z.string(),
      // The product's strike-through price, only where it is above this
      // line's own. Optional so a cart from an older server still parses.
      compare_at_price: z.string().nullish(),
      line_subtotal: z.string(),
      available: z.number().nullable(),
    }),
  ),
  subtotal: z.string(),
  discount_code: z.string().nullable(),
  // Everything off the goods, the bundle discount included.
  discount_amount: z.string(),
  // The "frequently bought together" share of `discount_amount`, shown as its
  // own line. Defaults so a cart from a server without bundles still parses.
  bundle_discount_amount: z.string().default('0.00'),
  tax_amount: z.string(),
  shipping_amount: z.string(),
  total: z.string(),
  // Gift cards are tender: `total` stays the order's value and the card pays
  // part of it, leaving `amount_due` to collect.
  gift_card_code: z.string().nullable(),
  gift_card_amount: z.string(),
  amount_due: z.string(),
  // Where the parcel is going and how, once the shopper has said. All four
  // default so a cart from a server that predates priced delivery still
  // parses - `shipping_amount` and `total` already carry delivery either way.
  shipping_postal_code: z.string().nullish().transform((value) => value ?? null),
  shipping_option: shippingOptionSchema.nullish().transform((value) => value ?? null),
  // True while delivery cannot be priced yet - a shop that asks for a PIN code
  // and has not been given one. The summary says "calculated later" rather
  // than showing a zero the shopper would read as free.
  shipping_pending: z.boolean().default(false),
  shipping_error: z.string().nullish().transform((value) => value ?? null),
  // The shopper's order instructions, typed on the cart page. Optional, so a
  // cart from a server that predates order notes still parses.
  note: z.string().nullish(),
});

export const checkoutResultSchema = z.object({
  order_id: z.string(),
  order_number: z.string(),
  status: z.string(),
  payment_method: z.string(),
  total: z.string(),
  payment: z.record(z.unknown()).nullable(),
  // Proves this browser placed the order, for following it without an
  // account. Absent from a server that predates push notifications.
  order_token: z.string().nullish(),
});

export type StorefrontContext = z.infer<typeof contextSchema>;

/** The shop's languages with every absent field filled in. */
export interface ResolvedStorefrontLanguages {
  /** The language this response was rendered in. */
  language: StorefrontLanguage;
  defaultLanguage: string;
  /** Enabled languages in switcher order; always contains `language`. */
  languages: StorefrontLanguage[];
  switcherEnabled: boolean;
  fallbackToDefault: boolean;
}

const ENGLISH: StorefrontLanguage = {
  code: 'en',
  locale: 'en-IN',
  name: 'English',
  native_name: 'English',
  direction: 'LTR',
};

/**
 * The context's language fields, read the way a server without i18n means
 * them: one language (the response's, else English), switcher on.
 */
export function resolveStorefrontLanguages(
  context: Pick<StorefrontContext, 'language' | 'default_language' | 'languages' | 'i18n'>,
): ResolvedStorefrontLanguages {
  const listed = context.languages ?? [];
  const language =
    context.language ??
    listed.find((entry) => entry.code === context.default_language) ??
    listed[0] ??
    ENGLISH;
  const languages = listed.some((entry) => entry.code === language.code) ? listed : [language, ...listed];
  return {
    language,
    defaultLanguage: context.default_language ?? languages[0].code,
    languages,
    switcherEnabled: context.i18n?.switcher_enabled ?? true,
    fallbackToDefault: context.i18n?.fallback_to_default ?? true,
  };
}

/**
 * The payment options a till should offer, from whatever context it was given.
 *
 * A context that carries `checkout_options` is believed outright - an empty
 * list is a merchant who switched everything off, and the till must say so
 * rather than invent a default. A context without the field is older than the
 * setting, and reads as it always did: cash on delivery, plus online payment
 * when a gateway was connected.
 */
export function resolveCheckoutOptions(
  context: Pick<StorefrontContext, 'checkout_options' | 'online_payments_enabled'> | undefined,
): CheckoutOption[] {
  if (!context) return [];
  if (context.checkout_options) return context.checkout_options;
  const legacy: CheckoutOption[] = [];
  if (context.online_payments_enabled) {
    legacy.push({
      method: 'RAZORPAY',
      instructions: null,
      fee: '0.00',
      min_order_amount: null,
      max_order_amount: null,
      whatsapp_phone: null,
    });
  }
  legacy.push({
    method: 'COD',
    instructions: null,
    fee: '0.00',
    min_order_amount: null,
    max_order_amount: null,
    whatsapp_phone: null,
  });
  return legacy;
}

/** Delivery switched off: what every shop was before it could be priced. */
export const NO_SHIPPING: StorefrontShippingInfo = {
  enabled: false,
  requires_pincode: false,
  cod_by_pincode: false,
  free_shipping_threshold: null,
  flat_rate: null,
};

/**
 * How the shop delivers, from whatever context it was given.
 *
 * A context without the field is older than the setting, and reads as
 * delivery switched off - which is exactly the till's old behaviour, so a
 * storefront ahead of its server asks nothing it cannot act on.
 */
export function resolveShipping(
  context: Pick<StorefrontContext, 'shipping'> | undefined,
): StorefrontShippingInfo {
  return context?.shipping ?? NO_SHIPPING;
}
export type StorefrontProduct = z.infer<typeof productDetailSchema>;
export type StorefrontProductSummary = z.infer<typeof productSummarySchema>;
export type StorefrontCategory = z.infer<typeof storefrontCategorySchema>;
export type StorefrontMetafield = z.infer<typeof storefrontMetafieldSchema>;
export type Cart = z.infer<typeof cartSchema>;

/**
 * The one cache key the storefront shell holds its context under.
 *
 * Exported because it has been got wrong: two dashboard screens invalidated
 * `['storefront', 'context']`, which is not a prefix of this and so matched
 * nothing at all. Anything outside the storefront should invalidate the broad
 * `['storefront']` instead - the preview shell keeps its own copy under a
 * different scope, and the narrow key would leave that one stale.
 */
export const STOREFRONT_CONTEXT_KEY = ['storefront', 'live', 'context'] as const;

export function fetchContext(host?: string) {
  const [path, options] = publicRead('/storefront/context', host);
  return apiRequest(path, contextSchema, options);
}

export const facetValueSchema = z.object({
  value: z.string(),
  label: z.string(),
  count: z.number(),
});

export const categoryFacetSchema = facetValueSchema.extend({
  id: z.string(),
  slug: z.string(),
});

/** A variant option - Size, Colour - and the values the results offer it in. */
export const optionFacetSchema = z.object({
  name: z.string(),
  values: z.array(facetValueSchema).default([]),
});

export const productFacetsSchema = z.object({
  total: z.number(),
  categories: z.array(categoryFacetSchema).default([]),
  brands: z.array(facetValueSchema).default([]),
  product_types: z.array(facetValueSchema).default([]),
  tags: z.array(facetValueSchema).default([]),
  options: z.array(optionFacetSchema).default([]),
  price: z.object({ min: z.string().nullable(), max: z.string().nullable() }),
  in_stock_count: z.number(),
});

export type FacetValue = z.infer<typeof facetValueSchema>;
export type OptionFacet = z.infer<typeof optionFacetSchema>;
export type CategoryFacet = z.infer<typeof categoryFacetSchema>;
export type ProductFacets = z.infer<typeof productFacetsSchema>;

/** The orderings the public listing accepts, mirroring `ProductSort`. */
export type StorefrontSort =
  | 'newest'
  | 'oldest'
  | 'name_asc'
  | 'name_desc'
  | 'price_asc'
  | 'price_desc';

/**
 * What a shopper has narrowed the catalog to.
 *
 * The arrays are multi-select: each value becomes a repeat of the same query
 * parameter, which is how the API reads "either of these" for one dimension.
 */
export interface StorefrontProductFilters {
  q?: string | null;
  /** Category *ids*. Slugs are what the address bar carries; pages resolve them. */
  categoryIds?: string[];
  brands?: string[];
  productTypes?: string[];
  tags?: string[];
  /**
   * Variant options, keyed by the merchant's own name for them:
   * `{ Colour: ['Indigo'], Size: ['M', 'L'] }`.
   */
  options?: Record<string, string[]>;
  minPrice?: string | null;
  maxPrice?: string | null;
  inStock?: boolean;
  /** Only products marked down from a compare-at price - a product tab's "On sale". */
  onSale?: boolean;
  /** Only these product ids - what a recently-viewed rail asks for. */
  ids?: string[];
}

/**
 * Every ordering the listing answers. `best_selling` (units sold) is asked for
 * by a product tabs section rather than offered in the search page's sort menu.
 */
export type StorefrontListingSort = StorefrontSort | 'best_selling';

export interface StorefrontProductQuery extends StorefrontProductFilters {
  page?: number;
  size?: number;
  /** Narrows to one category, which is what a pinned product section asks for. */
  categoryId?: string | null;
  sort?: StorefrontListingSort | null;
  host?: string;
}

/** The filter half of a listing request, as query parameters. */
function filterParams(filters: StorefrontProductFilters): URLSearchParams {
  const query = new URLSearchParams();
  if (filters.q?.trim()) query.set('q', filters.q.trim());
  const repeated: [string, string[] | undefined][] = [
    ['category_id', filters.categoryIds],
    ['brand', filters.brands],
    ['product_type', filters.productTypes],
    ['tag', filters.tags],
    ['id', filters.ids],
  ];
  for (const [name, values] of repeated) {
    for (const value of values ?? []) query.append(name, value);
  }
  // Variant options share one parameter because their names are the
  // merchant's words, not the API's - `?option=Colour:Indigo&option=Size:M`.
  for (const [name, values] of Object.entries(filters.options ?? {})) {
    for (const value of values) query.append('option', `${name}:${value}`);
  }
  if (filters.minPrice) query.set('min_price', filters.minPrice);
  if (filters.maxPrice) query.set('max_price', filters.maxPrice);
  // Only ever sent as `true`: the toggle is "hide sold out", and `false` would
  // ask the API for the sold-out products alone.
  if (filters.inStock) query.set('in_stock', 'true');
  // The same: only ever "marked down", never "full price only".
  if (filters.onSale) query.set('on_sale', 'true');
  return query;
}

export function fetchStorefrontProducts({
  page = 1,
  size,
  categoryId,
  sort,
  host,
  ...filters
}: StorefrontProductQuery = {}) {
  const query = filterParams(filters);
  query.set('page', String(page));
  if (size) query.set('size', String(size));
  if (categoryId) query.append('category_id', categoryId);
  if (sort) query.set('sort', sort);
  const [path, options] = publicRead(`/storefront/products?${query}`, host);
  return apiRequest(path, pageSchema(productSummarySchema), options);
}

/**
 * The filter panel's options and counts for the *same* narrowing.
 *
 * Fetched separately from the products so paging never re-counts facets: the
 * panel does not change as a shopper walks through pages of one result set.
 */
export function fetchStorefrontFacets(filters: StorefrontProductFilters = {}, host?: string) {
  const [path, options] = publicRead(`/storefront/products/facets?${filterParams(filters)}`, host);
  return apiRequest(path, productFacetsSchema, options);
}

export function fetchStorefrontCategories(host?: string) {
  const [path, options] = publicRead('/storefront/categories', host);
  return apiRequest(path, z.array(storefrontCategorySchema), options);
}

/** What the contact form posts. Emailed to the merchant; nothing is stored. */
export interface ContactMessage {
  name: string;
  email: string;
  phone?: string;
  message?: string;
}

export function sendContactMessage(message: ContactMessage) {
  return apiRequest(
    '/storefront/contact',
    z.record(z.unknown()),
    publicOptions({ method: 'POST', body: message }),
  );
}

/**
 * The bookings section's calendar, for one service on one day.
 *
 * Computed on the server from the merchant's own section - which services
 * exist, how long they run, when the shop is open, how many people it can see
 * at once - and never here. That is the whole point of asking: a slot list this
 * browser worked out for itself would be a list of times it merely *hoped* were
 * free, and the shopper would find out at the moment of booking.
 *
 * `start_at` and `end_at` are instants. The store's own zone comes back beside
 * them so the storefront can print the shop's ten o'clock rather than the
 * shopper's, wherever they are reading from.
 */
export const bookingSlotSchema = z.object({
  start_at: z.string(),
  end_at: z.string(),
  remaining: z.number(),
  available: z.boolean(),
  /**
   * Who is free at this time, on a section that books by person. Empty on a
   * section that does not (`staff_mode` none), and on a server that predates
   * people - which is the same thing to the picker: nobody to narrow by.
   */
  team_member_ids: z.array(z.string()).default([]),
});

/**
 * One session of a person who books by token (an OPD queue): no times to
 * pick, just the next number in the queue and roughly when it will be seen.
 * `remaining` is null when the session has no cap.
 */
export const tokenSessionSchema = z.object({
  team_member_id: z.string(),
  team_member_name: z.string(),
  starts_at: z.string(),
  ends_at: z.string(),
  issued: z.number(),
  next_token: z.number(),
  remaining: z.number().nullable().default(null),
  estimated_at: z.string(),
  available: z.boolean(),
});

export const bookingAvailabilitySchema = z.object({
  section_id: z.string(),
  service_id: z.string(),
  service_name: z.string(),
  day: z.string(),
  timezone: z.string(),
  duration_minutes: z.number(),
  slots: z.array(bookingSlotSchema),
  /**
   * `tokens` when the person asked about books by token: `sessions` are then
   * their queues for the day and `slots` is empty. With "any available", the
   * token people's sessions come beside everyone else's slots (`slots`). A
   * server that predates tokens says neither, which reads as plain slots.
   */
  mode: z.enum(['slots', 'tokens']).catch('slots').default('slots'),
  sessions: z.array(tokenSessionSchema).default([]),
});

/**
 * Rupees, as the server serialises them - a number, or the decimal string a
 * `Decimal` becomes - read as the two-place string the gateway and
 * `formatMoney` both take. Anything that is not an amount reads as nothing.
 */
export const moneyText = z
  .union([z.number(), z.string()])
  .nullish()
  .transform((value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' && !value.trim()) return null;
    const amount = Number(value);
    return Number.isFinite(amount) ? amount.toFixed(2) : null;
  });

/** Nullable text, read as `null` when the server leaves it out. */
const nullableText = z
  .string()
  .nullish()
  .transform((value) => value ?? null);

/**
 * How a booking is paid for: in full or a deposit online, at the venue, the
 * shopper's choice, or (on an appointment) with a package credit. Mirrors
 * `BookingPaymentMode` on the server.
 */
export const BOOKING_PAYMENT_MODES = [
  'FULL',
  'DEPOSIT',
  'PAY_AT_VENUE',
  'CUSTOMER_CHOICE',
  'PACKAGE',
] as const;
export type BookingPaymentMode = (typeof BOOKING_PAYMENT_MODES)[number];

/**
 * What a booking asks to be paid now, and the gateway's order to pay it with.
 *
 * `required` false is a booking the shop takes without money up front - paid
 * at the venue, or settled with a package credit. `hold_expires_at` is when
 * the held slot goes back on sale if nothing is paid. `provider_payload` is
 * the same `{ razorpay_order_id, key_id }` an order's checkout hands over.
 */
export const bookingPaymentSchema = z.object({
  required: z.boolean().default(false),
  mode: nullableText,
  amount: moneyText.transform((value) => value ?? '0.00'),
  total: moneyText,
  currency: z
    .string()
    .nullish()
    .transform((value) => value || 'INR'),
  hold_expires_at: nullableText,
  provider_payload: z
    .record(z.unknown())
    .nullish()
    .transform((value) => value ?? null),
});

export type BookingPayment = z.infer<typeof bookingPaymentSchema>;

export const bookingConfirmationSchema = z.object({
  id: z.string(),
  service_name: z.string(),
  starts_at: z.string(),
  ends_at: z.string(),
  status: z.string(),
  /** Who the shopper will see - the one picked, or the one the shop assigned. */
  team_member_name: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  /** Where it happens: the shop, online, or the shopper's own address. */
  location: z.enum(['place', 'online', 'address']).catch('place').default('place'),
  /** The patient's number, for a person who books by token; `starts_at` is then an estimate. */
  token_number: z
    .number()
    .nullish()
    .transform((value) => value ?? null),
  /**
   * What is due now, on a shop that takes booking payments. Null - and absent
   * on a server or a sample shop that predates them - is nothing to pay here.
   */
  payment: bookingPaymentSchema.nullish(),
  /** Lets this browser pay for, or ask after, the booking without an account. */
  access_token: z.string().nullish(),
  /** `AWAITING`, `PAID`, `DEPOSIT_PAID`, `NOT_REQUIRED`... - see `AppointmentPaymentStatus`. */
  payment_status: z.string().nullish(),
  /** The package a credit was taken from, when the booking used one. */
  package_purchase_id: z.string().nullish(),
  /** Credits left on that package after this booking. */
  sessions_left: z.number().nullish(),
});

/**
 * Where a held booking stands, as the pay and poll calls answer: its status
 * (`AWAITING_PAYMENT` until paid), the payment's own, and what has been paid.
 */
export const bookingPaymentStateSchema = z.object({
  id: z.string(),
  status: z.string(),
  payment_status: nullableText,
  amount_paid: moneyText,
  hold_expires_at: nullableText,
});

export type BookingPaymentState = z.infer<typeof bookingPaymentStateSchema>;

export type TokenSession = z.infer<typeof tokenSessionSchema>;
export type BookingSlot = z.infer<typeof bookingSlotSchema>;
export type BookingAvailability = z.infer<typeof bookingAvailabilitySchema>;
export type BookingConfirmation = z.infer<typeof bookingConfirmationSchema>;

export interface BookingAvailabilityQuery {
  sectionId: string;
  serviceId: string;
  /** The day, as `YYYY-MM-DD` in the store's timezone. */
  day: string;
  /**
   * One person's diary, on a section that books by person. Left off in
   * "choose" mode it means "any available": a time is free when anybody who
   * offers the service is.
   */
  teamMemberId?: string | null;
  host?: string;
}

export function fetchBookingAvailability({
  sectionId,
  serviceId,
  day,
  teamMemberId,
  host,
}: BookingAvailabilityQuery) {
  const query = new URLSearchParams({ section_id: sectionId, service_id: serviceId, day });
  if (teamMemberId) query.set('team_member_id', teamMemberId);
  return apiRequest(
    `/storefront/bookings/availability?${query}`,
    bookingAvailabilitySchema,
    publicOptions({}, host),
  );
}

/** What the bookings form posts. The slot is re-checked server-side before it is taken. */
export interface BookingDraft {
  section_id: string;
  service_id: string;
  /**
   * The instant chosen, as an ISO string - one of the `start_at`s just
   * offered. Left out when a token is taken instead (`session_starts_at`).
   */
  start_at?: string;
  /**
   * For a person who books by token: the session (its `starts_at`) to take
   * the next number in, with `team_member_id` naming whose session it is.
   */
  session_starts_at?: string;
  name: string;
  /** Optional: a booking needs only a name and a phone number. */
  email?: string;
  phone: string;
  notes?: string;
  /** The person picked; null in "choose" mode means "any available". */
  team_member_id?: string | null;
  /** Where to come to, for a service done at the shopper's address. */
  customer_address?: string | null;
  /**
   * The shopper's answers to the section's own questions, keyed by question
   * id. The server checks them against the section's *visible* questions -
   * required ones present, a select's value one of its options - and copies
   * each question's label beside its answer, so renaming a question later
   * does not rewrite what an earlier patient was asked.
   */
  answers?: Record<string, string | boolean>;
  /**
   * The shopper agreed to updates about this appointment on WhatsApp, at the
   * `phone` above. Ignored by the server without a phone; left out unless true.
   */
  whatsapp_opt_in?: boolean;
  /**
   * The shopper's answer to "Pay now or at the venue?", on a service that
   * lets them choose. Left out everywhere else - the server decides.
   */
  pay_now?: boolean;
  /** Settle this booking with a credit from the signed-in shopper's package. */
  use_package?: boolean;
}

/**
 * Books with the shopper's session when they are signed in, so the booking is
 * filed under their account and shows under "My bookings" whatever email they
 * typed. Signed out, no token goes and nothing changes. A session that has
 * lapsed past renewing is not a reason to lose a booking: it goes again as a
 * guest's, which is filed by its email as it always was.
 */
export async function createBooking(booking: BookingDraft) {
  try {
    return await apiRequest(
      '/storefront/bookings',
      bookingConfirmationSchema,
      customerOptions({ method: 'POST', body: booking }),
    );
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401) throw error;
    return apiRequest(
      '/storefront/bookings',
      bookingConfirmationSchema,
      publicOptions({ method: 'POST', body: booking }),
    );
  }
}

const bookingPath = (bookingId: string, rest: string, token: string) =>
  `/storefront/bookings/${encodeURIComponent(bookingId)}${rest}?${new URLSearchParams({ token })}`;

/**
 * A fresh payment for a held booking - "Pay now" again after the gateway was
 * closed. Refused with 409 `BOOKING_HOLD_EXPIRED` once the hold has lapsed and
 * the slot is back on sale.
 */
export async function startBookingPayment(bookingId: string, token: string) {
  const result = await apiRequest(
    bookingPath(bookingId, '/payment', token),
    z.object({ payment: bookingPaymentSchema }),
    publicOptions({ method: 'POST' }),
  );
  return result.payment;
}

/**
 * Ask the server to check a payment the gateway says went through. Not the
 * only way it is confirmed - the signed webhook is - but it is the quick one,
 * so the shopper reads "paid" now rather than on their next visit.
 */
export function verifyBookingPayment(bookingId: string, token: string, providerPaymentId: string) {
  return apiRequest(
    bookingPath(bookingId, '/payment/verify', token),
    bookingPaymentStateSchema,
    publicOptions({ method: 'POST', body: { provider_payment_id: providerPaymentId } }),
  );
}

/** Where a booking stands, by its access token - for polling a payment. */
export function getBooking(bookingId: string, token: string) {
  return apiRequest(bookingPath(bookingId, '', token), bookingPaymentStateSchema, publicOptions());
}

/**
 * A pack of sessions the shop sells - "5 physio sessions". Empty on a shop
 * that does not take booking payments, which hides everything that sells one.
 */
export const storefrontPackageSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  service_ids: z.array(z.string()).default([]),
  sessions: z.number(),
  price: moneyText.transform((value) => value ?? '0.00'),
  validity_days: z
    .number()
    .nullish()
    .transform((value) => value ?? null),
});

export type StorefrontPackage = z.infer<typeof storefrontPackageSchema>;

export async function listPackages(host?: string | null) {
  const result = await apiRequest(
    '/storefront/packages',
    z.object({ items: z.array(storefrontPackageSchema).default([]) }),
    publicOptions({}, host),
  );
  return result.items;
}

/** One package the signed-in shopper bought, and what is left on it. */
export const customerPackageSchema = z
  .object({
    id: z.string(),
    package_id: nullableText,
    package_name: z.string().default(''),
    service_ids: z.array(z.string()).default([]),
    sessions_total: z.number().default(0),
    sessions_used: z.number().default(0),
    sessions_left: z.number().nullish(),
    /** `AWAITING_PAYMENT`, `ACTIVE`, `EXHAUSTED`, `EXPIRED`, `CANCELLED` or `REFUNDED`. */
    status: z.string().default('ACTIVE'),
    expires_at: nullableText,
    purchased_at: nullableText,
  })
  .transform((row) => ({
    ...row,
    sessions_left: row.sessions_left ?? Math.max(0, row.sessions_total - row.sessions_used),
  }));

export type CustomerPackage = z.infer<typeof customerPackageSchema>;

/** A purchase started: its id, and the payment to take for it. */
export const packagePurchaseStartSchema = z.object({
  purchase_id: z.string(),
  payment: bookingPaymentSchema.nullish().transform((value) => value ?? null),
});

export type PackagePurchaseStart = z.infer<typeof packagePurchaseStartSchema>;

/** Buy a package; signed-in shoppers only. */
export function buyPackage(packageId: string) {
  return apiRequest(
    '/storefront/account/packages',
    packagePurchaseStartSchema,
    customerOptions({ method: 'POST', body: { package_id: packageId } }),
  );
}

export function verifyPackagePurchase(purchaseId: string, providerPaymentId: string) {
  return apiRequest(
    `/storefront/account/packages/${encodeURIComponent(purchaseId)}/verify`,
    customerPackageSchema,
    customerOptions({ method: 'POST', body: { provider_payment_id: providerPaymentId } }),
  );
}

/** The signed-in shopper's packages, with the sessions left on each. */
export async function listMyPackages() {
  const result = await apiRequest(
    '/storefront/account/packages',
    z.object({ items: z.array(customerPackageSchema).default([]) }),
    customerOptions(),
  );
  return result.items;
}

/**
 * Today's token queues, for the waiting-room "Now serving" board. Numbers
 * only - never a patient's name. Rate-limited on the server, so poll it no
 * more than every fifteen seconds or so.
 */
export const publicQueueSessionSchema = z.object({
  starts_at: z.string(),
  ends_at: z.string(),
  now_serving: z.number().nullable().default(null),
  last_issued: z.number().nullable().default(null),
  waiting: z.number().default(0),
});

export const publicQueueSchema = z.object({
  store_name: z.string(),
  timezone: z.string(),
  people: z.array(
    z.object({
      name: z.string(),
      slug: z.string(),
      sessions: z.array(publicQueueSessionSchema),
    }),
  ),
});

export type PublicQueueSession = z.infer<typeof publicQueueSessionSchema>;
export type PublicQueue = z.infer<typeof publicQueueSchema>;

/** `GET /storefront/queue` - every token person's queues today, or one person's by slug. */
export function fetchPublicQueue(teamMember?: string | null, host?: string | null) {
  const query = teamMember ? `?${new URLSearchParams({ team_member: teamMember })}` : '';
  return apiRequest(`/storefront/queue${query}`, publicQueueSchema, publicOptions({}, host));
}

/**
 * Records marketing consent for this store. Says the same thing either way.
 * `name` names a customer the address is new for, and nobody else.
 */
export function subscribeToNewsletter(email: string, name?: string) {
  return apiRequest(
    '/storefront/subscribe',
    z.record(z.unknown()),
    publicOptions({ method: 'POST', body: name ? { email, name } : { email } }),
  );
}

// --- Product reviews -------------------------------------------------------
/**
 * What customers said about one product.
 *
 * Every route here answers 404 on a shop whose plan does not include reviews,
 * and the storefront reads that as "draw nothing" rather than as an error -
 * see `ProductReviews`. Reading is public; writing is the signed-in shopper's,
 * so those calls carry the customer token the account pages carry.
 */
export const storefrontReviewSchema = z.object({
  id: z.string(),
  rating: z.number(),
  title: z.string().nullable(),
  body: z.string(),
  author_name: z.string(),
  verified_purchase: z.boolean(),
  photos: z.array(z.string()).default([]),
  merchant_reply: z.string().nullable(),
  merchant_reply_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const REVIEW_STATUSES = ['PENDING', 'PUBLISHED', 'HIDDEN'] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

export const storefrontOwnReviewSchema = storefrontReviewSchema.extend({
  status: z.enum(REVIEW_STATUSES),
});

export const reviewSummarySchema = z.object({
  average: z.number().nullable(),
  count: z.number(),
  distribution: z.record(z.number()).default({}),
});

export const productReviewsSchema = z.object({
  summary: reviewSummarySchema,
  items: z.array(storefrontReviewSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
});

export type StorefrontReview = z.infer<typeof storefrontReviewSchema>;
export type StorefrontOwnReview = z.infer<typeof storefrontOwnReviewSchema>;
export type ReviewSummary = z.infer<typeof reviewSummarySchema>;
export type ProductReviews = z.infer<typeof productReviewsSchema>;
export type ReviewSortOrder = 'newest' | 'highest' | 'lowest' | 'photos';

export interface ProductReviewsQuery {
  page?: number;
  pageSize?: number;
  sort?: ReviewSortOrder;
  /** Only reviews of this many stars. The summary ignores it. */
  rating?: number | null;
}

/** What a shopper posts, and what an edit replaces it with. */
export interface ReviewDraft {
  rating: number;
  title?: string | null;
  body: string;
  photos?: string[];
}

/** The signed-in shopper's session, on this shop. See `account/api/account.ts`. */
function customerOptions(extra: RequestOptions = {}): RequestOptions {
  return {
    cache: 'no-store',
    ...extra,
    auth: 'customer',
    headers: { ...storefrontHeaders(), ...extra.headers },
  };
}

const reviewsPath = (slug: string) => `/storefront/products/${encodeURIComponent(slug)}/reviews`;

export function fetchProductReviews(slug: string, query: ProductReviewsQuery = {}) {
  const params = new URLSearchParams({
    page: String(query.page ?? 1),
    page_size: String(query.pageSize ?? 5),
    sort: query.sort ?? 'newest',
  });
  if (query.rating) params.set('rating', String(query.rating));
  return apiRequest(`${reviewsPath(slug)}?${params}`, productReviewsSchema, publicOptions());
}

/** The shopper's own review of this product, whatever its status; null when they have none. */
export function fetchMyProductReview(slug: string) {
  return apiRequest(
    `${reviewsPath(slug)}/mine`,
    storefrontOwnReviewSchema.nullable(),
    customerOptions(),
  );
}

/** 409 `REVIEW_EXISTS` when they already have one - edit that instead. */
export function submitProductReview(slug: string, draft: ReviewDraft) {
  return apiRequest(
    reviewsPath(slug),
    storefrontOwnReviewSchema,
    customerOptions({ method: 'POST', body: draft }),
  );
}

export function updateMyProductReview(slug: string, draft: ReviewDraft) {
  return apiRequest(
    `${reviewsPath(slug)}/mine`,
    storefrontOwnReviewSchema,
    customerOptions({ method: 'PUT', body: draft }),
  );
}

/** Review photos per review; the server refuses a fifth. */
export const MAX_REVIEW_PHOTOS = 4;

/**
 * One photo for a review, uploaded before the review is sent; the URL it
 * returns goes in the review's `photos`. Its own endpoint rather than the
 * order-request upload, because a reviewer need not have an order to attach it
 * to. 400 `INVALID_PHOTO` for a file that is not an image the server takes.
 */
export function uploadReviewPhoto(slug: string, file: File) {
  const body = new FormData();
  body.append('file', file);
  return apiRequest(
    `${reviewsPath(slug)}/photos`,
    z.object({ url: z.string() }),
    customerOptions({ method: 'POST', body }),
  );
}

// --- Product questions and back-in-stock alerts --------------------------------
/** One published question, with the shop's answer when it has one. */
export const storefrontQuestionSchema = z.object({
  id: z.string(),
  question: z.string(),
  answer: z.string().nullable(),
  /** "Priya S." - never the full name or the email. */
  asker_name: z.string(),
  answered_at: z.string().nullable(),
  created_at: z.string(),
});

export type StorefrontQuestion = z.infer<typeof storefrontQuestionSchema>;

export const productQuestionsSchema = z.object({
  items: z.array(storefrontQuestionSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
});

export type ProductQuestions = z.infer<typeof productQuestionsSchema>;

export interface QuestionDraft {
  question: string;
  /** Left out by a signed-in shopper, whose own are used. */
  name?: string;
  email?: string;
  /** The honeypot: never shown, so only a script fills it. */
  website?: string;
}

const questionsPath = (slug: string) => `/storefront/products/${encodeURIComponent(slug)}/questions`;

/** Published questions only; answers 404 on a plan without reviews. */
export function fetchProductQuestions(slug: string, query: { page?: number; pageSize?: number } = {}) {
  const params = new URLSearchParams({
    page: String(query.page ?? 1),
    page_size: String(query.pageSize ?? 5),
  });
  return apiRequest(`${questionsPath(slug)}?${params}`, productQuestionsSchema, publicOptions());
}

/** Sent with the shopper's token when they are signed in, and without it otherwise. */
export function askProductQuestion(slug: string, draft: QuestionDraft) {
  return apiRequest(
    questionsPath(slug),
    z.object({ status: z.string() }),
    customerOptions({ method: 'POST', body: draft }),
  );
}

/** "Notify me when available" for one sold-out variant. */
export function subscribeStockAlert(
  variantId: string,
  email?: string,
  pushSubscription?: BrowserPushSubscription | null,
) {
  return apiRequest(
    '/storefront/stock-alerts',
    z.object({ subscribed: z.boolean() }),
    customerOptions({
      method: 'POST',
      body: {
        variant_id: variantId,
        ...(email ? { email } : {}),
        // This browser too, as a notification - where the shop offers push.
        ...(pushSubscription ? { push_subscription: pushSubscription } : {}),
      },
    }),
  );
}

// --- Push notifications ----------------------------------------------------
/** `PushSubscription.toJSON()`, the part of it the server reads. */
export interface BrowserPushSubscription {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

const pushReceiptSchema = z.object({
  subscribed: z.boolean(),
  scope: z.enum(['order', 'appointment', 'customer']),
});

/**
 * Follow one order from this browser - a guest proves it is theirs with the
 * checkout's `orderToken` - or, signed in with no order, every order of theirs.
 */
export function subscribeToOrderPush(
  subscription: BrowserPushSubscription,
  order?: { orderId: string; orderToken?: string | null },
) {
  return apiRequest(
    '/storefront/push/subscriptions',
    pushReceiptSchema,
    customerOptions({
      method: 'POST',
      body: {
        subscription,
        ...(order ? { order_id: order.orderId, order_token: order.orderToken ?? undefined } : {}),
      },
    }),
  );
}

/** Whether this browser already hears about `orderId` (or anything, without one). */
export function fetchOrderPushStatus(endpoint: string, orderId?: string) {
  return apiRequest(
    '/storefront/push/status',
    z.object({ following: z.boolean() }),
    publicOptions({ method: 'POST', body: { endpoint, ...(orderId ? { order_id: orderId } : {}) } }),
  );
}

/** Follow one booking: its confirmation, cancellation and reminders. */
export function subscribeToAppointmentPush(
  subscription: BrowserPushSubscription,
  appointment: { appointmentId: string; appointmentToken?: string | null },
) {
  return apiRequest(
    '/storefront/push/subscriptions',
    pushReceiptSchema,
    customerOptions({
      method: 'POST',
      body: {
        subscription,
        appointment_id: appointment.appointmentId,
        appointment_token: appointment.appointmentToken ?? undefined,
      },
    }),
  );
}

/** Whether this browser already hears about the booking `appointmentId`. */
export function fetchAppointmentPushStatus(endpoint: string, appointmentId: string) {
  return apiRequest(
    '/storefront/push/status',
    z.object({ following: z.boolean() }),
    publicOptions({ method: 'POST', body: { endpoint, appointment_id: appointmentId } }),
  );
}

/** Forget this browser at this shop, for every order it followed. */
export function unsubscribeFromPush(endpoint: string) {
  return apiRequest(
    '/storefront/push/unsubscribe',
    z.object({ following: z.boolean() }),
    publicOptions({ method: 'POST', body: { endpoint } }),
  );
}

export function fetchStorefrontProduct(slug: string) {
  const [path, options] = publicRead(`/storefront/products/${slug}`);
  return apiRequest(path, productDetailSchema, options);
}

/**
 * The two rails under a product page.
 *
 * Either can come back empty - a catalog with no categories has no neighbours
 * to show, and a store with no orders has nothing to say about what sells
 * together - so the page hides the rail rather than showing an empty heading.
 */
export const relatedProductsSchema = z.object({
  similar: z.array(productSummarySchema).default([]),
  also_liked: z.array(productSummarySchema).default([]),
});

export type StorefrontRelatedProducts = z.infer<typeof relatedProductsSchema>;

export function fetchStorefrontRelatedProducts(slug: string, limit = 12) {
  const [path, options] = publicRead(`/storefront/products/${slug}/related?limit=${limit}`);
  return apiRequest(path, relatedProductsSchema, options);
}

/**
 * The "frequently bought together" set on a product page: the product itself
 * first, then the bundled products a shopper can buy now. `null` when there
 * is no bundle. The discount is what the cart takes off once every item is in
 * it - the cart prices it, so this is only ever a preview of that figure.
 */
export const productBundleSchema = z.object({
  items: z.array(
    z.object({
      product_id: z.string(),
      name: z.string(),
      slug: z.string(),
      image_url: z.string().nullable(),
      image_focal: focalPointSchema.nullish(),
      variant_id: z.string(),
      price: z.string(),
      compare_at_price: z.string().nullable(),
      anchor: z.boolean(),
    }),
  ),
  discount_type: z.enum(['none', 'percent', 'fixed']),
  discount_value: z.string(),
});

export type StorefrontBundle = z.infer<typeof productBundleSchema>;

export function fetchStorefrontProductBundle(slug: string) {
  return apiRequest(
    `/storefront/products/${slug}/bundle`,
    productBundleSchema.nullable(),
    publicOptions(),
  );
}

export function createCart() {
  return apiRequest('/storefront/cart', cartSchema, publicOptions({ method: 'POST' }));
}

export function fetchCart(token: string) {
  return apiRequest(`/storefront/cart/${token}`, cartSchema, publicOptions());
}

export function setCartItem(token: string, variantId: string, quantity: number) {
  return apiRequest(
    `/storefront/cart/${token}/items`,
    cartSchema,
    publicOptions({ method: 'PUT', body: { variant_id: variantId, quantity } }),
  );
}

/**
 * Put a code on the cart, or take it off with `null`.
 *
 * Carries the shopper's token when one is signed in, as checkout does: that is
 * what lets a one-per-customer code they have already spent be refused here,
 * where they typed it, rather than at the till.
 */
export function applyDiscount(token: string, code: string | null) {
  return apiRequest(`/storefront/cart/${token}/discount`, cartSchema, {
    cache: 'no-store',
    method: 'PUT',
    body: { code },
    auth: 'customer',
    headers: storefrontHeaders(),
  });
}

/** `null` removes the card. Nothing is deducted until the order is placed. */
export function applyGiftCard(token: string, code: string | null) {
  return apiRequest(
    `/storefront/cart/${token}/gift-card`,
    cartSchema,
    publicOptions({ method: 'PUT', body: { code } }),
  );
}

/**
 * What the delivery question is asked for: this basket, that PIN code.
 *
 * A product page asks before there is a basket, for one of one variant, and
 * sends `variantId` instead of `cartToken`. One of the two is always sent.
 */
export interface ShippingOptionsQuery {
  cartToken?: string;
  variantId?: string;
  /** A six-digit PIN code. Validated by the caller; the server refuses anything else. */
  postalCode: string;
  /** Prices cash on delivery in when it is the way the shopper means to pay. */
  paymentMethod?: CheckoutMethod | null;
}

/**
 * The ways this basket can be delivered to one PIN code.
 *
 * Asked again whenever the basket, the PIN code or the payment method
 * changes, because the answer is priced for all three - a courier that is
 * free over a threshold stops being free when a line is removed.
 */
export function fetchShippingOptions(
  { cartToken, variantId, postalCode, paymentMethod }: ShippingOptionsQuery,
  host?: string,
) {
  const query = new URLSearchParams({ postal_code: postalCode });
  if (cartToken) query.set('cart_token', cartToken);
  else if (variantId) query.set('variant_id', variantId);
  if (paymentMethod) query.set('payment_method', paymentMethod);
  return apiRequest(
    `/storefront/shipping/options?${query}`,
    shippingQuoteSchema,
    publicOptions({}, host),
  );
}

/**
 * What the cart is told about delivery. Every field optional: a PIN code on
 * its own clears the chosen option and re-prices for the default, an option
 * code on its own picks one of the ways just offered.
 */
export interface CartShippingUpdate {
  postal_code?: string;
  option_code?: string;
  payment_method?: string;
}

/** Tell the cart where it is going, or how. Totals come back re-priced. */
export function setCartShipping(token: string, update: CartShippingUpdate, host?: string) {
  return apiRequest(
    `/storefront/cart/${token}/shipping`,
    cartSchema,
    publicOptions({ method: 'PUT', body: update }, host),
  );
}

/**
 * Keep the shopper's order instructions on the cart. Null or blank removes
 * them. Checkout copies the note to the order unless it sends one of its own.
 */
export function setCartNote(token: string, note: string | null, host?: string) {
  return apiRequest(
    `/storefront/cart/${token}/note`,
    cartSchema,
    publicOptions({ method: 'PUT', body: { note } }, host),
  );
}

export interface CheckoutPayload {
  cart_token: string;
  customer: { name: string; email: string; phone?: string; marketing_opt_in: boolean };
  address: {
    name: string;
    phone?: string;
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
  };
  /** Add the typed address to the signed-in shopper's address book. */
  save_address?: boolean;
  payment_method: CheckoutMethod;
  note?: string;
  /**
   * Where the shopper came from. `'whatsapp'` when the basket was opened from
   * the bot's "Checkout on website" link, so the server can file the order
   * under that channel; left out for an ordinary visit.
   */
  channel?: CheckoutChannel;
}

/** The sales channels a checkout can name itself as. */
export type CheckoutChannel = 'storefront' | 'whatsapp';

/**
 * Place the order.
 *
 * The one public storefront call that *is* customer-authenticated - optionally.
 * With no shopper signed in, no token goes out and this behaves exactly as it
 * always has. With one signed in, the token is what tells the server to file
 * the order against their account rather than against whatever email is in the
 * body, and to save the delivery address to their book.
 */
export function checkout(payload: CheckoutPayload) {
  return apiRequest('/storefront/checkout', checkoutResultSchema, {
    method: 'POST',
    body: payload,
    auth: 'customer',
    headers: storefrontHeaders(),
  });
}

/**
 * An order waiting to be paid, opened from a link rather than from the till.
 *
 * What a shopper who ordered in a WhatsApp chat is sent: a secure web page for
 * the one thing a chat cannot do, which is take a card. The token in the link
 * is what proves the shopper was handed it - an order id on its own would let
 * anyone open anyone's payment. `payment_intent` is the same gateway handoff
 * the checkout returns for an online payment, so the same opener serves both.
 */
export const orderPaymentSchema = z.object({
  order_number: z.string(),
  amount_due: z.string(),
  currency: z.string().default('INR'),
  status: z.enum(['pending', 'paid', 'cancelled']),
  payment_intent: z.record(z.unknown()).nullable(),
});

export type OrderPayment = z.infer<typeof orderPaymentSchema>;

export function fetchOrderPayment(orderId: string, token: string, host?: string) {
  const query = new URLSearchParams({ token });
  return apiRequest(
    `/storefront/orders/${encodeURIComponent(orderId)}/pay?${query}`,
    orderPaymentSchema,
    publicOptions({}, host),
  );
}

// --- Pages and the blog ---------------------------------------------------

/** A published page, named. What a footer or a sitemap lists. */
export const storefrontPageLinkSchema = z.object({
  title: z.string(),
  handle: z.string(),
});

export const storefrontPageSchema = z.object({
  title: z.string(),
  handle: z.string(),
  // Loose here, parsed by `sections/schema.ts` - the same treatment the theme's
  // own sections get, so a page degrades to a shorter page rather than a broken
  // one when it holds a section this build does not know.
  sections: z.array(z.record(z.unknown())),
  // False when the merchant's own sections carry the heading. Defaulted so a
  // response from before the setting existed still renders its title.
  show_title: z.boolean().default(true),
  seo_title: z.string().nullable(),
  seo_description: z.string().nullable(),
  published_at: z.string().nullable(),
  localized: localizedInfoSchema.nullish(),
});

export const storefrontBlogSchema = z.object({
  title: z.string(),
  handle: z.string(),
  description: z.string().nullable(),
  seo_title: z.string().nullable(),
  seo_description: z.string().nullable(),
});

export const storefrontPostSummarySchema = z.object({
  title: z.string(),
  handle: z.string(),
  excerpt: z.string().nullable(),
  image_url: z.string().nullable(),
  // The cover's focal point, for the cropped index card and post header;
  // optional, so a sample post needs none.
  image_focal: focalPointSchema.nullish(),
  author_name: z.string().nullable(),
  tags: z.array(z.string()),
  published_at: z.string().nullable(),
  // Seeded with the store's theme and not yet adopted: with no cover of its
  // own it wears the preview's artwork (`postCoverUrl`) rather than nothing.
  is_sample: z.boolean().optional(),
});

export const storefrontPostSchema = storefrontPostSummarySchema.extend({
  blog_handle: z.string(),
  blog_title: z.string(),
  body: z.string(),
  seo_title: z.string().nullable(),
  seo_description: z.string().nullable(),
  localized: localizedInfoSchema.nullish(),
});

export type StorefrontPageLink = z.infer<typeof storefrontPageLinkSchema>;
export type StorefrontPage = z.infer<typeof storefrontPageSchema>;
export type StorefrontBlog = z.infer<typeof storefrontBlogSchema>;
export type StorefrontPostSummary = z.infer<typeof storefrontPostSummarySchema>;
export type StorefrontPost = z.infer<typeof storefrontPostSchema>;

export function fetchStorefrontPages(host?: string) {
  const [path, options] = publicRead('/storefront/pages', host);
  return apiRequest(path, z.array(storefrontPageLinkSchema), options);
}

/** A legal link for the shop's footer, mirroring `StorefrontPolicyLink`. */
export const storefrontPolicyLinkSchema = z.object({
  kind: z.enum(['PRIVACY', 'TERMS', 'REFUND', 'SHIPPING', 'COOKIE']),
  title: z.string(),
  /** `STORE`: the shop's own page, by handle. `PLATFORM`: a NeonCarts policy, by slug. */
  source: z.enum(['STORE', 'PLATFORM']),
  handle: z.string(),
});

export type StorefrontPolicyLink = z.infer<typeof storefrontPolicyLinkSchema>;

export function fetchStorefrontPolicies(host?: string) {
  const [path, options] = publicRead('/storefront/policies', host);
  return apiRequest(path, z.array(storefrontPolicyLinkSchema), options);
}

export function fetchStorefrontPage(handle: string, host?: string) {
  const [path, options] = publicRead(`/storefront/pages/${encodeURIComponent(handle)}`, host);
  return apiRequest(path, storefrontPageSchema, options);
}

export function fetchStorefrontBlogs(host?: string) {
  return apiRequest('/storefront/blogs', z.array(storefrontBlogSchema), publicOptions({}, host));
}

export function fetchStorefrontBlog(handle: string, host?: string) {
  return apiRequest(
    `/storefront/blogs/${encodeURIComponent(handle)}`,
    storefrontBlogSchema,
    publicOptions({}, host),
  );
}

export interface StorefrontPostQuery {
  page?: number;
  size?: number;
  /** Narrows the index to one tag, which is what a tag chip links to. */
  tag?: string | null;
  host?: string;
}

export function fetchStorefrontPosts(
  blogHandle: string,
  { page = 1, size, tag, host }: StorefrontPostQuery = {},
) {
  const query = new URLSearchParams({ page: String(page) });
  if (size) query.set('size', String(size));
  if (tag) query.set('tag', tag);
  return apiRequest(
    `/storefront/blogs/${encodeURIComponent(blogHandle)}/posts?${query}`,
    pageSchema(storefrontPostSummarySchema),
    publicOptions({}, host),
  );
}

export function fetchStorefrontPost(blogHandle: string, postHandle: string, host?: string) {
  return apiRequest(
    `/storefront/blogs/${encodeURIComponent(blogHandle)}/posts/${encodeURIComponent(postHandle)}`,
    storefrontPostSchema,
    publicOptions({}, host),
  );
}
